import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  useEditor,
  EditorContent,
  ReactNodeViewRenderer,
  NodeViewContent,
  NodeViewWrapper
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import TextAlign from '@tiptap/extension-text-align';
import CharacterCount from '@tiptap/extension-character-count';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { common, createLowlight } from 'lowlight';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

// shadcn/ui components
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Upload } from 'lucide-react';

// Custom Extensions
import { SmilieReplacer } from '../extensions/SmilieReplacer';
import { Video } from '../extensions/video';
import AiContentNode from '../extensions/AiContentNode';
import Toolbar from './Toolbar';
import { toast } from 'sonner';

const CodeBlockComponent = ({ node, updateAttributes, extension }) => {
  const defaultLanguage = node.attrs.language;

  return (
    <NodeViewWrapper className="relative">
      <select
        contentEditable={false}
        defaultValue={defaultLanguage}
        onChange={event => updateAttributes({ language: event.target.value })}
        className="absolute right-2 top-2 rounded-md px-1.5 py-1 text-sm bg-gray-100 border border-gray-300"
      >
        <option value="null">auto</option>
        <option disabled>—</option>
        {extension.options.lowlight.listLanguages().map((lang, index) => (
          <option key={index} value={lang}>
            {lang}
          </option>
        ))}
      </select>
      <pre>
        <NodeViewContent as="code" />
      </pre>
    </NodeViewWrapper>
  );
};

const ResizableImageComponent = ({ node, updateAttributes, selected }) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const imgRef = useRef(null);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(imgRef.current?.offsetWidth || 0);
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!imgRef.current) return;
      const diff = e.clientX - startX;
      const newWidth = Math.max(100, Math.min(startWidth + diff, 1000));
      updateAttributes({ width: newWidth });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, startX, startWidth, updateAttributes]);

  const alignment = node.attrs.textAlign || 'left';

  return (
    <NodeViewWrapper
      className="relative block group"
      style={{ textAlign: alignment }}
    >
      <div className="relative inline-block">
        <img
          ref={imgRef}
          src={node.attrs.src}
          alt={node.attrs.alt || ''}
          width={node.attrs.width || 'auto'}
          className="rounded-lg max-w-full h-auto"
          style={{
            width: node.attrs.width ? `${node.attrs.width}px` : 'auto',
            display: 'block'
          }}
        />
        {selected && (
          <>
            <div className="absolute inset-0 border-2 border-blue-500 rounded-lg pointer-events-none" />
            <div
              className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-12 bg-blue-500 cursor-ew-resize rounded-r hover:bg-blue-600 flex items-center justify-center"
              onMouseDown={handleMouseDown}
            >
              <div className="w-0.5 h-6 bg-white" />
            </div>
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

const lowlight = createLowlight(common);

/**
 * TasksAiEditor - A rich text editor with AI capabilities for Tasks
 * This is a specialized version of AiEditor with image upload support
 *
 * @param {Object} props
 * @param {string} props.value - The HTML content of the editor
 * @param {Function} props.onChange - Callback when content changes
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.characterLimit - Optional character limit
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onEditorReady - Callback when editor is initialized
 * @param {boolean} props.showVoiceButton - Show voice input button (default: true)
 *
 * @example
 * <TasksAiEditor
 *   value={content}
 *   onChange={setContent}
 *   placeholder="Write something..."
 *   characterLimit={5000}
 * />
 */
const TasksAiEditor = ({
  value,
  onChange,
  placeholder = 'Write something...',
  characterLimit,
  className = '',
  onEditorReady,
  showVoiceButton = true
}) => {
  const { company } = useAuth();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);

  const [videoUrl, setVideoUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        codeBlock: false
      }),
      Placeholder.configure({
        placeholder
      }),
      SmilieReplacer,
      Typography,
      Video,
      AiContentNode.configure({
        content: '',
        status: 'idle'
      }),
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: element => element.getAttribute('width'),
              renderHTML: attributes => {
                if (!attributes.width) return {};
                return { width: attributes.width };
              }
            },
            textAlign: {
              default: 'left',
              parseHTML: element => element.style.textAlign || 'left',
              renderHTML: attributes => {
                if (!attributes.textAlign) return {};
                return { style: `text-align: ${attributes.textAlign}` };
              }
            }
          };
        },
        addNodeView() {
          return ReactNodeViewRenderer(ResizableImageComponent);
        }
      }).configure({
        HTMLAttributes: {
          class: 'rounded-lg'
        }
      }),
      CodeBlockLowlight.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeBlockComponent);
        }
      }).configure({
        lowlight
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https'
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph', 'image']
      }),
      Color,
      TextStyle,
      ...(characterLimit !== undefined
        ? [CharacterCount.configure({ limit: characterLimit })]
        : [])
    ],
    [placeholder, characterLimit]
  );

  const editorConfig = useMemo(
    () => ({
      editorProps: {
        attributes: {
          class: 'prose prose-sm prose-neutral max-w-none focus:outline-none min-h-[200px]'
        },
        parseOptions: {
          preserveWhitespace: 'full'
        },
        scrollMargin: 160,
        scrollThreshold: 160
      },
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      extensions,
      content: value,
      onUpdate: ({ editor }) => {
        const htmlContent = editor.getHTML();
        onChange(htmlContent);
      }
    }),
    [extensions, value, onChange]
  );

  const editor = useEditor(editorConfig);

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addVideo = () => {
    if (videoUrl) {
      editor.commands.setVideo(videoUrl);
      setVideoUrl('');
      setShowVideoModal(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error('Image must be less than 100MB');
      return;
    }

    if (!company?.id) {
      toast.error('Company information not available');
      console.error('Company ID is missing');
      return;
    }

    setUploadingImage(true);
    setUploadProgress(0);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const folderPath = `${company.id}/space/images/tasks`;

      console.log('Requesting upload URL for:', { fileName, folderPath });

      // Get signed upload URL from Edge Function
      const { data: urlData, error: urlError } = await supabase.functions.invoke(
        'generate-upload-url',
        {
          body: {
            fileName: fileName,
            contentType: file.type,
            folderPath: folderPath,
          },
        }
      );

      console.log('Upload URL response:', { urlData, urlError });

      if (urlError || !urlData?.signedUrl || !urlData?.publicUrl) {
        console.error('Failed to get upload URL:', urlError);
        throw new Error(urlError?.message || 'Failed to get upload URL');
      }

      // Upload to GCS using XMLHttpRequest for progress tracking
      console.log('Starting XHR upload to:', urlData.signedUrl);
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            console.log('Upload progress:', percentComplete);
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          console.log('XHR load event, status:', xhr.status);
          if (xhr.status === 200) {
            resolve();
          } else {
            console.error('XHR failed with status:', xhr.status, xhr.responseText);
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', (e) => {
          console.error('XHR error event:', e);
          reject(new Error('Upload failed'));
        });

        xhr.addEventListener('abort', () => {
          console.error('XHR abort event');
          reject(new Error('Upload cancelled'));
        });

        xhr.open('PUT', urlData.signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      console.log('Upload completed successfully, inserting image:', urlData.publicUrl);

      // Insert image into editor
      editor.chain().focus().setImage({ src: urlData.publicUrl }).run();

      // Reset file input
      const fileInput = document.getElementById('task-image-upload');
      if (fileInput) fileInput.value = '';

      setShowImageModal(false);
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const addLink = () => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
      setLinkUrl('');
      setShowLinkModal(false);
    }
  };

  const percentage = useMemo(() => {
    if (characterLimit !== undefined && editor) {
      return Math.round(
        (100 / characterLimit) * editor.storage.characterCount.characters()
      );
    }
    return 0;
  }, [characterLimit, editor?.storage.characterCount?.characters()]);

  return (
    <div className={`flex flex-col border border-gray-300 rounded-md overflow-hidden bg-white ${className}`}>
      <div className="sticky top-0 z-10 bg-white">
        <Toolbar
          editor={editor}
          setShowVideoModal={setShowVideoModal}
          setShowImageModal={setShowImageModal}
          setShowLinkModal={setShowLinkModal}
          showVoiceButton={showVoiceButton}
        />
      </div>
      <EditorContent editor={editor} className="overflow-y-auto flex-1 p-6" />

      {characterLimit && (
        <div
          className={`p-4 text-xs items-center gap-2 flex border-t border-gray-200 ${
            editor.storage.characterCount.characters() === characterLimit
              ? 'text-red-600'
              : 'text-gray-600'
          }`}
        >
          <svg height="20" width="20" viewBox="0 0 20 20" className="text-blue-600">
            <circle r="10" cx="10" cy="10" fill="#e5e7eb" />
            <circle
              r="5"
              cx="10"
              cy="10"
              fill="transparent"
              stroke="currentColor"
              strokeWidth="10"
              strokeDasharray={`calc(${percentage} * 31.4 / 100) 31.4`}
              transform="rotate(-90) translate(-20)"
            />
            <circle r="6" cx="10" cy="10" fill="white" />
          </svg>
          {editor.storage.characterCount.characters()} / {characterLimit} characters
          <span className="mx-2">•</span>
          {editor.storage.characterCount.words()} words
        </div>
      )}

      {/* Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Video URL</DialogTitle>
          </DialogHeader>
          <Input
            value={videoUrl}
            onChange={e => setVideoUrl(e.target.value)}
            placeholder="Enter video URL"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVideoModal(false)}>
              Cancel
            </Button>
            <Button onClick={addVideo}>Insert Video</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Upload Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!uploadingImage ? (
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="task-image-upload"
                />
                <label
                  htmlFor="task-image-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="h-12 w-12 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Click to upload image</p>
                    <p className="text-sm text-muted-foreground">
                      PNG, JPG, GIF up to 100MB
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 py-8">
                <LoadingSpinner />
                <p className="text-sm font-medium">Uploading image...</p>
                {uploadProgress > 0 && (
                  <div className="w-full max-w-xs">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>Progress</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowImageModal(false)}
              disabled={uploadingImage}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Link URL</DialogTitle>
          </DialogHeader>
          <Input
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            placeholder="Enter link URL"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkModal(false)}>
              Cancel
            </Button>
            <Button onClick={addLink}>Insert Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksAiEditor;
